import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { PriceBreakdownDto } from '@/api-sdk';
import { FgDialog } from '@/components';
import { GlobalComponentType, global } from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, typographyStyles } from '@/styles';

import '@/components/fg-dialog';

@customElement('fg-price-details')
@localized()
export class FgPriceDetails extends LitElement {
  private fgDialogRef: Ref<FgDialog> = createRef();

  @property({ type: Object })
  priceBreakdown?: PriceBreakdownDto | null;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  render() {
    if (!this.priceBreakdown) {
      return null;
    }

    const {
      variants,
      printOptions,
      handling,
      totals,
      productionSpeedDiscount,
    } = this.priceBreakdown;
    const hasMultipleVariants = variants.length > 1;

    const hasSurcharges = variants.some(e => e.surcharges.length > 0),
      { layout } = this.globalContext;

    return html`
      <fg-dialog part=${layout} ${ref(this.fgDialogRef)}>
        <span slot="title">
          ${layout === 'retail'
            ? t('headers.priceDetails')
            : t('headers.priceDetailsSummary')}
        </span>
        <div class="body">
          <table>
            ${hasMultipleVariants
              ? variants.map(
                  (variant, index) => html`
                    ${layout === 'wholesale'
                      ? html`
                          ${index > 0
                            ? html`
                                <tr class="border">
                                  <td colspan="2"></td>
                                </tr>
                              `
                            : nothing}
                          <tr>
                            <td>
                              <strong>
                                ${variant?.color?.name}
                                ${variant?.version?.name}
                                ${variant?.size?.name
                                  ? html`
                                      - ${variant?.size?.name}
                                    `
                                  : nothing}
                              </strong>
                            </td>
                          </tr>
                        `
                      : nothing}
                    <tr>
                      <td>
                        <div class="price-info">
                          ${this.globalContext.layout === 'retail'
                            ? html`
                                ${variant?.color?.name}
                                ${variant?.version?.name} -
                                ${variant?.size?.name}
                              `
                            : nothing}
                          <span>
                            ${variant.explanation &&
                            this.globalContext.layout === 'retail'
                              ? html`
                                  (${variant.explanation})
                                `
                              : nothing}
                          </span>
                          <span>
                            ${variant.explanation &&
                            this.globalContext.layout === 'wholesale'
                              ? html`
                                  ${variant.explanation}
                                `
                              : nothing}
                          </span>
                        </div>
                      </td>
                      <td>${variant.totalPrice.formatted}</td>
                    </tr>
                    ${printOptions &&
                    printOptions.length > 0 &&
                    variant.allInUnitPrice
                      ? html`
                          <tr>
                            <span class="unit-price">
                              ${variant.allInUnitPrice.formatted}
                              ${t('labels.pricePerUnit')}
                            </span>
                          </tr>
                        `
                      : nothing}
                  `,
                )
              : html`
                  <tr>
                    <td>
                      <div class="price-info">
                        ${t('labels.itemPrice')}
                        <span>
                          ${variants[0].explanation
                            ? html`
                                (${variants[0].explanation})
                              `
                            : nothing}
                        </span>
                      </div>
                    </td>
                    <td>${variants[0].totalPrice.formatted}</td>
                  </tr>
                `}
            ${printOptions.map(
              (printOption, index) => html`
                <tr class="border">
                  <td colspan="2"></td>
                </tr>
                <tr>
                  <td colspan="2">
                    <strong>
                      ${t('labels.printNumber', { index: index + 1 })}
                    </strong>
                  </td>
                </tr>
                ${printOption.colors.map(
                  (color, index) => html`
                    <tr>
                      <td>
                        <div class="price-info">
                          ${printOption.colors.length === 1
                            ? t('labels.oneOrMoreColors')
                            : t('labels.colorNumber', { index: index + 1 })}
                          <span>
                            <span>
                              ${color.explanation
                                ? html`
                                    (${color.explanation})
                                  `
                                : nothing}
                            </span>
                          </span>
                        </div>
                      </td>

                      <td>${color.totalPrice.formatted}</td>
                    </tr>
                  `,
                )}
                <tr>
                  <td>
                    <div class="price-info">
                      ${t('labels.setupCosts')}
                      <span>
                        ${printOption.setup.explanation
                          ? html`
                              (${printOption.setup.explanation})
                            `
                          : nothing}
                      </span>
                    </div>
                  </td>
                  <td>${printOption.setup.totalPrice.formatted}</td>
                </tr>
              `,
            )}
            ${hasSurcharges
              ? html`
                  <tr class="border">
                    <td colspan="2"></td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <strong>${t('labels.surcharges')}</strong>
                    </td>
                  </tr>
                  ${variants.map(
                    variant => html`
                      ${variant.surcharges.map(
                        surcharge => html`
                          <tr>
                            <td>
                              <div class="price-info">
                                ${surcharge.name}
                                <span>
                                  ${surcharge.explanation
                                    ? html`
                                        (${surcharge.explanation})
                                      `
                                    : nothing}
                                </span>
                              </div>
                            </td>
                            <td>${surcharge.totalPrice.formatted}</td>
                          </tr>
                        `,
                      )}
                    `,
                  )}
                `
              : nothing}
            <tr class="border">
              <td colspan="2"></td>
            </tr>
            ${handling
              ? html`
                  <tr class="processing-cost">
                    <td>
                      <div class="price-info">
                        ${t('labels.processingCosts')}
                        <span>
                          <span>
                            ${handling.explanation
                              ? html`
                                  (${handling.explanation})
                                `
                              : nothing}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td>${handling.totalPrice.formatted}</td>
                  </tr>
                `
              : nothing}
            ${productionSpeedDiscount
              ? html`
                  <tr class="production-speed-discount">
                    <td>
                      <span>
                        ${t(
                          layout === 'retail'
                            ? 'content.productionSpeed.productionSpeedDeliveryDiscount'
                            : 'content.productionSpeed.productionSpeedDiscount',
                        )}:
                      </span>
                    </td>
                    <td>-${productionSpeedDiscount.formatted}</td>
                  </tr>
                `
              : nothing}

            <tr class="total">
              <td><strong>${t('labels.totalPriceExclVat')}</strong></td>
              <td>
                <strong>${totals.excludingVat.formatted}</strong>
              </td>
            </tr>
            ${totals.includingVat && layout === 'retail'
              ? html`
                  <tr>
                    <td>${t('labels.totalPriceInclVat')}</td>
                    <td>${totals.includingVat.formatted}</td>
                  </tr>
                `
              : nothing}
          </table>
        </div>
      </fg-dialog>
    `;
  }

  openDialog() {
    this.fgDialogRef.value?.open();
  }

  static styles = [
    baseStyles,
    typographyStyles,
    css`
      table {
        font-size: 1rem;
        width: 100%;
      }

      td {
        text-align: right;
        padding: 4px 0;
      }

      :host([part='wholesale']) strong {
        text-transform: capitalize;
        font-size: 1.125rem;
        font-family: var(--fg-font-family--brand);
        font-weight: 500;
      }

      td:first-child {
        text-align: left;
        width: 70%;
      }

      :host([part='wholesale']) .price-info,
      :host([part='wholesale']) .unit-price {
        color: var(--fg-gray--600);
      }

      td span {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
      }

      tr.border td {
        padding: 10px 0;
      }

      tr.border td:after {
        content: '';
        display: block;
        width: 100%;
        height: 1px;
        top: 50%;
        transform: translateY(-50%);
        background: var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      :host([part='wholesale']) tr.total strong {
        font-weight: 600;
      }

      tr.total td:last-child {
        font-size: 1.25rem;
      }

      .production-speed-discount {
        & td,
        & td span {
          color: var(--fg-error, var(--fg-fallback-error));
        }
      }

      :host([part='wholesale']) .processing-cost td {
        padding-bottom: 16px;
      }

      :host([part='wholesale']) .processing-cost .price-info,
      :host([part='wholesale']) .processing-cost td {
        color: var(--fg-gray--800);
      }

      :host([part='wholesale']) .processing-cost .price-info::before {
        content: none;
      }

      :host([part='wholesale']) .total td {
        padding: 0;
      }

      :host([part='wholesale']) .total td:last-child strong {
        font-size: 1.375rem;
        font-weight: 600;
        line-height: 1.9375rem;
      }

      @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
        .price-info {
          display: flex;
          flex-direction: column;
        }
      }
    `,
  ];
}
